import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { BRANDS } from "@/data/brands";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact sample-request modal — smaller commitment than the full B2B inquiry
// form. Just enough to ship physical samples: contact, address, brands.
// -----------------------------------------------------------------------------
export default function SampleRequestModal({ open, onClose }) {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("sample.");
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    address: "",
    brands: [],
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggleBrand = (slug) => {
    setForm((f) => ({
      ...f,
      brands: f.brands.includes(slug)
        ? f.brands.filter((b) => b !== slug)
        : [...f.brands, slug],
    }));
  };

  const label = (key, fb) => {
    const v = t(`sample.${key}`);
    return isTranslated(v) ? v : fb;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.brands.length === 0) {
      toast.error(label("errNoBrand", "Please pick at least one brand"));
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/sample-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setDone(true);
      toast.success(label("success", "Sample request received — we'll be in touch."));
    } catch (err) {
      toast.error(label("errServer", "Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({
      full_name: "", company: "", email: "", phone: "", country: "",
      address: "", brands: [], notes: "",
    });
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[110] overflow-y-auto bg-black/70 backdrop-blur-sm"
          onClick={reset}
          data-testid="sample-modal"
        >
          <div className="min-h-full flex items-start sm:items-center justify-center px-4 py-10 sm:py-14">
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
            <img
              aria-hidden
              src="/hupla-emblem.svg"
              alt=""
              className="pointer-events-none select-none absolute -right-24 -bottom-24 w-[340px] max-w-none opacity-[0.06]"
            />
            <button
              onClick={reset}
              className="absolute z-20 top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-black/5 text-black/60"
              aria-label="Close"
              data-testid="sample-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative p-8 md:p-10">
              {done ? (
                <div className="text-center py-6" data-testid="sample-modal-success">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 mb-5">
                    <Check className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-editorial italic mb-3">
                    {label("thanks", "Samples on the way.")}
                  </h3>
                  <p className="text-sm text-black/70 max-w-sm mx-auto">
                    {label("thanksBody", "Our export team will confirm your shipping address within 2 business days and dispatch the samples straight after.")}
                  </p>
                  <button
                    onClick={reset}
                    className="mt-8 inline-flex rounded-full px-6 py-2 text-sm font-medium bg-black text-white"
                  >
                    {label("close", "Close")}
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-[0.65rem] font-bold uppercase tracking-[0.32em] text-[#C41947] mb-2">
                    {label("kicker", "Sample Request")}
                  </p>
                  <h3 className="text-2xl md:text-3xl font-editorial italic leading-tight mb-2">
                    {label("title", "Try before you list.")}
                  </h3>
                  <p className="text-sm text-black/60 mb-6">
                    {label("sub", "Tell us where to ship and which brands to include — we cover freight to serious accounts.")}
                  </p>

                  <form onSubmit={submit} className="space-y-3" data-testid="sample-form">
                    <div className="grid md:grid-cols-2 gap-3">
                      <input required minLength={2} placeholder={label("phName", "Full name *")} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" data-testid="sample-full-name" />
                      <input required minLength={2} placeholder={label("phCompany", "Company *")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" data-testid="sample-company" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <input required type="email" placeholder={label("phEmail", "Business email *")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" data-testid="sample-email" />
                      <input placeholder={label("phPhone", "Phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" data-testid="sample-phone" />
                    </div>
                    <input required placeholder={label("phCountry", "Country / market *")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" data-testid="sample-country" />
                    <textarea required minLength={8} rows={2} placeholder={label("phAddress", "Shipping address *")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm resize-none" data-testid="sample-address" />

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-black/60 mb-2">
                        {label("brandsLabel", "Brands to include *")}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {BRANDS.map((b) => {
                          const selected = form.brands.includes(b.slug);
                          return (
                            <button
                              type="button"
                              key={b.slug}
                              onClick={() => toggleBrand(b.slug)}
                              data-testid={`sample-brand-${b.slug}`}
                              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${selected ? "bg-black text-white border-black" : "bg-white text-black/70 border-black/15 hover:border-black/40"}`}
                            >
                              {selected && <Check className="w-3 h-3" />}
                              {b.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <textarea rows={2} placeholder={label("phNotes", "Notes (optional)")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm resize-none" data-testid="sample-notes" />

                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="sample-submit"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-black text-white disabled:opacity-60 hover:opacity-90 transition-opacity"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {busy ? label("sending", "Sending…") : label("submit", "Request Samples")}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
